// src/utils/planCalculations.ts
// ─────────────────────────────────────────────────────────────────────────────
// Pure calculation functions for the "Your Plan" movement (Brief 6).
// All functions are stateless — import and use from any screen.
// ─────────────────────────────────────────────────────────────────────────────

import { ClientData, StandingOrder } from '../types/ClientData';

// ── Future value of a lump sum ─────────────────────────────────────────────
export function fvLump(pv: number, annualRate: number, years: number): number {
  return pv * Math.pow(1 + annualRate / 100, years);
}

// ── Future value of monthly contributions (end-of-month, annual rate as %) ─
export function fvContributions(monthlyAmount: number, annualRate: number, years: number): number {
  if (annualRate === 0 || years <= 0) return monthlyAmount * 12 * Math.max(years, 0);
  const r = annualRate / 100 / 12;
  const n = years * 12;
  return monthlyAmount * ((Math.pow(1 + r, n) - 1) / r);
}

// ── Mortgage remaining balance after N payments ────────────────────────────
export function mortgageBalanceAfter(
  principal: number, annualRate: number, termMonths: number, paymentsMade: number
): number {
  if (principal <= 0) return 0;
  if (annualRate === 0) return Math.max(0, principal - (principal / termMonths) * paymentsMade);
  const r = annualRate / 100 / 12;
  const pmt = (principal * r) / (1 - Math.pow(1 + r, -termMonths));
  const bal = principal * Math.pow(1 + r, paymentsMade) - pmt * ((Math.pow(1 + r, paymentsMade) - 1) / r);
  return Math.max(0, bal);
}

// ── Mortgage monthly payment ───────────────────────────────────────────────
export function calcMortgagePayment(principal: number, annualRate: number, termMonths: number): number {
  if (principal <= 0 || termMonths <= 0) return 0;
  if (annualRate === 0) return principal / termMonths;
  const r = annualRate / 100 / 12;
  return (principal * r) / (1 - Math.pow(1 + r, -termMonths));
}

// ── Months to payoff mortgage with overpayment ─────────────────────────────
export function mortgagePayoffMonths(
  principal: number, annualRate: number, termMonths: number,
  extraMonthly: number, annualLumpSum: number
): number {
  if (principal <= 0) return 0;
  if (annualRate === 0) {
    const stdPmt = principal / termMonths;
    return Math.ceil(principal / (stdPmt + extraMonthly));
  }
  const r = annualRate / 100 / 12;
  const standardPayment = calcMortgagePayment(principal, annualRate, termMonths);
  let balance = principal;
  let months = 0;
  while (balance > 0.01 && months < termMonths + 600) {
    balance = balance * (1 + r) - (standardPayment + extraMonthly);
    months++;
    // December lump sum (month 12, 24, 36...)
    if (months % 12 === 0 && annualLumpSum > 0) {
      balance = Math.max(0, balance - annualLumpSum);
    }
    if (balance <= 0.01) break;
  }
  return months;
}

// ── Total interest saved with overpayment vs standard ──────────────────────
export function totalInterestSaved(
  principal: number, annualRate: number, termMonths: number,
  extraMonthly: number, annualLumpSum: number
): number {
  if (principal <= 0 || annualRate <= 0) return 0;
  const r = annualRate / 100 / 12;
  const standardPayment = calcMortgagePayment(principal, annualRate, termMonths);

  // Standard total interest
  let bal = principal;
  let stdInterest = 0;
  for (let m = 0; m < termMonths && bal > 0.01; m++) {
    const interest = bal * r;
    stdInterest += interest;
    bal = bal + interest - standardPayment;
  }

  // Overpayment total interest
  bal = principal;
  let opInterest = 0;
  let months = 0;
  while (bal > 0.01 && months < termMonths + 600) {
    const interest = bal * r;
    opInterest += interest;
    bal = bal + interest - (standardPayment + extraMonthly);
    months++;
    if (months % 12 === 0 && annualLumpSum > 0) {
      bal = Math.max(0, bal - annualLumpSum);
    }
  }

  return Math.max(0, stdInterest - opInterest);
}

// ── Grossed-up pension from dividend reduction ─────────────────────────────
export function grossedUpPension(dividendReduction: number, ctRate: number): number {
  if (ctRate >= 1) return dividendReduction; // safety
  return dividendReduction / (1 - ctRate);
}

// ── Annual income at drawdown rate from pot ────────────────────────────────
export function drawdownIncome(pot: number, rate: number): number {
  return pot * rate;
}

// Effective CT rate has moved to src/taxConfig.ts as `effectiveCTRate`.
// Import from '../taxConfig' going forward.

// ── Generate Standing Orders from plan decisions ───────────────────────────
export function generateStandingOrders(data: ClientData): StandingOrder[] {
  const orders: StandingOrder[] = [];

  // HMRC tax reserve
  if (data.monthlyHMRC > 0) {
    orders.push({
      label: 'HMRC tax reserve',
      amount: data.monthlyHMRC,
      recipient: 'Dedicated savings account',
      startDate: 'Immediate',
    });
  }

  // Sinking funds total
  if (data.sinkingFundsTotal > 0) {
    orders.push({
      label: 'Sinking funds',
      amount: data.sinkingFundsTotal,
      recipient: 'Sinking fund account',
      startDate: 'Immediate',
      note: `${data.sinkingFundsDetail.length} categories`,
    });
  }

  // EF top-up (if not complete)
  if (data.planEfMonthlyTopUp > 0) {
    orders.push({
      label: 'Emergency fund top-up',
      amount: data.planEfMonthlyTopUp,
      recipient: 'EF savings account',
      startDate: 'Immediate',
      endDate: data.planEfCompleteDate || undefined,
      note: data.planEfCompleteDate
        ? `Completes ${data.planEfCompleteDate}. Redirects to ${data.planEfRedirectTo} after.`
        : undefined,
    });
  }

  // Debt snowball extra
  if (data.planDebtExtraMonthly > 0) {
    orders.push({
      label: 'Debt snowball extra',
      amount: data.planDebtExtraMonthly,
      recipient: 'Smallest debt first',
      startDate: 'Immediate',
      note: 'Rolls to next debt on clearance',
    });
  }

  // Employer pension (existing + dividend reduction if committed)
  const totalPension = (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  if (totalPension > 0) {
    orders.push({
      label: 'Employer pension contribution',
      amount: totalPension,
      recipient: 'Pension provider',
      startDate: 'Next payroll',
      note: data.planDividendReductionCommitted
        ? `Includes £${Math.round(data.planPensionFromDividendReduction)}/mo from reduced dividends — confirm in Session 2`
        : undefined,
    });
  }

  // ISA
  if (data.planIsaMonthlyContribution > 0) {
    orders.push({
      label: 'ISA standing order',
      amount: data.planIsaMonthlyContribution,
      recipient: 'ISA provider',
      startDate: 'Immediate',
    });
  }

  // Mortgage overpayment
  if (data.planMortgageOverpayment > 0) {
    orders.push({
      label: 'Mortgage overpayment',
      amount: data.planMortgageOverpayment,
      recipient: 'Mortgage lender',
      startDate: 'Immediate',
      endDate: data.planMortgagePayoffDate || undefined,
      note: data.planMortgagePayoffAge > 0
        ? `Mortgage paid off age ${data.planMortgagePayoffAge}. Then redirects to ${data.planMortgageRedirectTo}.`
        : undefined,
    });
  }

  return orders;
}
